import IconChevronDown from "@/app/(site)/_components/Atoms/Icons/IconChevronDown"
import Text from "@/app/(site)/_components/Atoms/Text"
import TextField from "@/app/(site)/_components/Molecules/TextField"
import { getLangCode } from "@/app/(site)/_utils/misc"
import { CustomerInfo } from "@/app/(site)/contexts/checkout/types"
import { getStatesByCountryCode } from "@/data/shipping-country-code"
import { parseGooglePlaceResult } from "@/lib/utils/checkout/googlePlaceApi"
import { t } from "@/translations"
import { CheckoutFormData, Fields } from "@/types/checkout-page"
import { Library } from "@googlemaps/js-api-loader"
import { Autocomplete, useLoadScript } from "@react-google-maps/api"
import getUnicodeFlagIcon from "country-flag-icons/unicode"
import { usePathname } from "next/navigation"
import React, { useEffect } from "react"
import { UseFormSetValue } from "react-hook-form"
import PlaceResult = google.maps.places.PlaceResult

export interface BillingFieldsProps {
    billingFields: Fields
    errors: any
    updateCustomerInfo: any
    customerInfo: CustomerInfo
    register: any
    fieldLabel: Map<string, string>
    fieldPlaceholder: Map<string, string>
    setValue: UseFormSetValue<CheckoutFormData>
    countries: Country[]
    fieldRefs: any
}

const scriptOptions = {
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"] as Library[]
}

export default function BillingFields({
    billingFields,
    errors,
    updateCustomerInfo,
    setValue,
    fieldLabel,
    fieldPlaceholder,
    customerInfo,
    register,
    countries,
    fieldRefs
}: BillingFieldsProps) {
    const { isLoaded, loadError } = useLoadScript(scriptOptions)
    const [autocomplete, setAutocomplete] =
        React.useState<google.maps.places.Autocomplete | null>(null)
    const langCode = getLangCode(usePathname().split("/")[1]) ?? ""
    const [tempEmail, setTempEmail] = React.useState<string>("")
    const countryCode =
        customerInfo.billingAddress.country || customerInfo.shippingAddress.country || ""
    const autoCompleteOptions: google.maps.places.AutocompleteOptions = {
        componentRestrictions: {
            country: customerInfo.billingAddress.country || ""
        },
        types: ["address"]
    }

    const [inputValue, setInputValue] = React.useState("")
    useEffect(() => {
        setInputValue(customerInfo.billingAddress.address1 || "")
    }, [customerInfo.billingAddress.address1])

    const onPlaceChanged = () => {
        if (isLoaded && autocomplete) {
            const place: PlaceResult = autocomplete.getPlace()
            const placeAddress = parseGooglePlaceResult(
                place,
                customerInfo.billingAddress.country
            )
            setInputValue(placeAddress.address1)
            updateCustomerInfo({
                billingAddress: {
                    ...customerInfo.billingAddress,
                    address1: placeAddress.address1,
                    zipCode: placeAddress.zipCode,
                    city: placeAddress.city,
                    state: placeAddress.state
                }
            })
            setValue("billingAddress.address1", placeAddress.address1)
            setValue("billingAddress.city", placeAddress.city)
            setValue("billingAddress.zipCode", placeAddress.zipCode)
            setValue("billingAddress.state", placeAddress.state)
        }
    }

    const onLoad = (autocompleteObj: google.maps.places.Autocomplete) => {
        setAutocomplete(autocompleteObj)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("billingAddress.address1", e.target.value)
        updateCustomerInfo({
            billingAddress: {
                ...customerInfo.billingAddress,
                address1: e.target.value
            }
        })
        setInputValue(e.target.value) // Control the input value
    }

    const states = getStatesByCountryCode(countries, countryCode)

    React.useEffect(() => {
        // Set country and state when component mounts
        const country =
            customerInfo.billingAddress.country ||
            customerInfo.shippingAddress.country ||
            ""

        updateCustomerInfo({
            billingAddress: {
                ...customerInfo.billingAddress,
                country: country,
                state: customerInfo.billingAddress.state
            }
        })

        setValue("billingAddress.country", country)
        setValue("billingAddress.state", customerInfo.billingAddress.state)
    }, [])

    return (
        <>
            {billingFields && (
                <div className="grid grid-cols-2 gap-x-24 gap-y-24">
                    <div className="col-span-2">
                        <Text
                            size="small"
                            weight="regular"
                            className={`mb-8 ${errors.billingAddress?.email?.ref ? "text-red-500" : "text-black"}`}
                        >
                            <label htmlFor="email">
                                {t(langCode, fieldLabel.get("Email") ?? "")}{" "}
                                {errors?.billingAddress?.email &&
                                    t(
                                        langCode,
                                        errors?.billingAddress?.email.message ?? ""
                                    )}
                            </label>
                        </Text>
                        <div ref={el => (fieldRefs.current["billingAddress.email"] = el)}>
                            <TextField
                                {...register("billingAddress.email")}
                                onChange={e => {
                                    const email = e.target.value
                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                                    setTempEmail(email)
                                    if (emailRegex.test(email) || email === "") {
                                        updateCustomerInfo({
                                            billingAddress: {
                                                ...customerInfo?.billingAddress,
                                                email: email
                                            }
                                        })
                                        setValue("billingAddress.email", email, {
                                            shouldValidate: true
                                        })
                                    }
                                }}
                                value={
                                    tempEmail || customerInfo?.billingAddress?.email || ""
                                }
                                placeholder={t(
                                    langCode,
                                    fieldPlaceholder.get("Email") ?? ""
                                )}
                                aria-label={t(langCode, "Email")}
                                className={
                                    errors.billingAddress?.email?.ref
                                        ? "border-red-500"
                                        : ""
                                }
                                theme="light"
                                name="billingAddress.email"
                                id="billingAddress.email"
                            />
                        </div>
                    </div>
                    <div className="col-span-2">
                        {/* LABEL */}
                        <Text
                            size="small"
                            weight="regular"
                            className={`mb-8 ${errors.billingAddress?.country?.ref ? "text-red-500" : "text-black"}`}
                        >
                            <label htmlFor="billingAddress.country">
                                {t(langCode, "Country")}{" "}
                                {t(
                                    langCode,
                                    errors?.billingAddress?.country?.message ?? ""
                                )}
                            </label>
                        </Text>
                        {/* SELECT */}
                        <div
                            className="relative w-full"
                            ref={el => (fieldRefs.current["billingAddress.country"] = el)}
                        >
                            <select
                                {...register("billingAddress.country")}
                                className={`w-full appearance-none rounded-full border py-16 pl-50 pr-24 ${errors.billingAddress?.country ? "border-red-500" : ""}`}
                                value={
                                    customerInfo?.billingAddress?.country ?? countryCode
                                }
                                onChange={e => {
                                    const newValue = e.target.value
                                    const defaultState = ""

                                    setValue("billingAddress.country", newValue, {
                                        shouldValidate: true
                                    })
                                    updateCustomerInfo({
                                        billingAddress: {
                                            ...customerInfo?.billingAddress,
                                            country: newValue,
                                            state: defaultState,
                                            address1: "",
                                            address2: "",
                                            zipCode: "",
                                            city: ""
                                        }
                                    })
                                    setValue("billingAddress.state", "")
                                    setValue("billingAddress.address1", "")
                                    setValue("billingAddress.address2", "")
                                    setValue("billingAddress.zipCode", "")
                                    setValue("billingAddress.city", "")
                                }}
                            >
                                {countries &&
                                    countries.map((country: any, index: number) => (
                                        <option key={index} value={country.country ?? ""}>
                                            {country.name}
                                        </option>
                                    ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center px-24 text-gray-700">
                                <span>
                                    {customerInfo?.billingAddress?.country ? (
                                        getUnicodeFlagIcon(
                                            customerInfo.billingAddress.country.toUpperCase()
                                        )
                                    ) : (
                                        <span>No country specified</span>
                                    )}
                                </span>
                            </div>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-24 text-gray-700">
                                <IconChevronDown className="h-20 w-20" />
                            </div>
                        </div>
                    </div>
                    <div className="col-span-1">
                        <Text size="small" weight="regular" className="mb-8">
                            <label
                                htmlFor="billingAddress.firstName"
                                className={`${errors?.billingAddress?.firstName?.ref ? "text-red-500" : "text-black"}`}
                            >
                                {!errors?.billingAddress?.firstName &&
                                    t(langCode, fieldLabel.get("Firstname") ?? "")}{" "}
                                {errors?.billingAddress?.firstName &&
                                    t(
                                        langCode,
                                        errors?.billingAddress?.firstName.message ?? ""
                                    )}
                            </label>
                        </Text>
                        <div
                            ref={el =>
                                (fieldRefs.current["billingAddress.firstName"] = el)
                            }
                        >
                            <TextField
                                {...register("billingAddress.firstName")}
                                placeholder={t(
                                    langCode,
                                    fieldPlaceholder.get("Firstname") ?? ""
                                )}
                                theme="light"
                                id="billingAddress.firstName"
                                name="billingAddress.firstName"
                                aria-label={t(langCode, "First Name")}
                                value={customerInfo?.billingAddress?.firstName ?? ""}
                                className={`w-full rounded-full border px-24 py-16 ${errors.billingAddress?.firstName ? "border-red-500" : ""}`}
                                onChange={e => {
                                    updateCustomerInfo({
                                        billingAddress: {
                                            ...customerInfo?.billingAddress,
                                            firstName: e.target.value
                                        }
                                    })
                                    setValue("billingAddress.firstName", e.target.value, {
                                        shouldValidate: true
                                    })
                                }}
                            />
                        </div>
                    </div>
                    <div className="col-span-1">
                        <Text size="small" weight="regular" className="mb-8">
                            <label
                                htmlFor="billingAddress.lastName"
                                className={` ${errors?.billingAddress?.lastName ? "text-red-500" : "text-black"}`}
                            >
                                {!errors?.billingAddress?.lastName &&
                                    t(langCode, fieldLabel.get("Lastname") ?? "")}{" "}
                                {errors?.billingAddress?.lastName &&
                                    t(
                                        langCode,
                                        errors?.billingAddress?.lastName?.message ?? ""
                                    )}
                            </label>
                        </Text>
                        <div
                            ref={el =>
                                (fieldRefs.current["billingAddress.lastName"] = el)
                            }
                        >
                            <TextField
                                {...register("billingAddress.lastName")}
                                onChange={e => {
                                    updateCustomerInfo({
                                        billingAddress: {
                                            ...customerInfo?.billingAddress,
                                            lastName: e.target.value
                                        }
                                    })
                                    setValue("billingAddress.lastName", e.target.value, {
                                        shouldValidate: true
                                    })
                                }}
                                value={customerInfo?.billingAddress?.lastName ?? ""}
                                placeholder={t(
                                    langCode,
                                    fieldPlaceholder.get("Lastname") ?? ""
                                )}
                                theme="light"
                                id="billingAddress.lastName"
                                name="billingAddress.lastName"
                                aria-label={t(langCode, "Last Name")}
                                className={`w-full rounded-full border px-24 py-16 ${errors.billingAddress?.lastName ? "border-red-500" : ""}`}
                            />
                        </div>
                    </div>
                    {Object.keys(billingFields).map((fieldName, index) => {
                        const field = billingFields[fieldName]

                        if (fieldName === "state" && states.length > 0) {
                            return (
                                <div className={"col-span-2"} key={index}>
                                    <Text
                                        size="small"
                                        weight="regular"
                                        className={`mb-8 ${errors.billingAddress?.state?.ref ? "text-red-500" : "text-black"}`}
                                    >
                                        <label htmlFor="billingAddress.state">
                                            {!errors?.billingAddress?.state &&
                                                t(
                                                    langCode,
                                                    fieldLabel.get("State") ?? ""
                                                )}
                                            {errors?.billingAddress?.state &&
                                                t(
                                                    langCode,
                                                    errors?.billingAddress?.state
                                                        ?.message ?? ""
                                                )}
                                        </label>
                                    </Text>
                                    <div
                                        className="relative w-full"
                                        ref={el =>
                                            (fieldRefs.current["billingAddress.state"] =
                                                el)
                                        }
                                    >
                                        <select
                                            {...register("billingAddress.state")}
                                            className={`w-full appearance-none rounded-full border px-24 py-16 ${errors.billingAddress?.state?.ref ? "border-red-500" : ""}`}
                                            value={customerInfo?.billingAddress?.state}
                                            onChange={e => {
                                                updateCustomerInfo({
                                                    billingAddress: {
                                                        ...customerInfo?.billingAddress,
                                                        state: e.target.value
                                                    }
                                                })
                                                setValue(
                                                    "billingAddress.state",
                                                    e.target.value,
                                                    {
                                                        shouldValidate: true
                                                    }
                                                )
                                            }}
                                        >
                                            <option value="">
                                                {t(langCode, "Select")}
                                            </option>
                                            {states.map((state: any, index: number) => (
                                                <option
                                                    key={state.name}
                                                    value={state.name ?? ""}
                                                >
                                                    {state.name}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-24 text-gray-700">
                                            {/* Here, add your icon for the dropdown arrow */}
                                            <IconChevronDown className="h-20 w-20" />
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                        if (isLoaded && fieldName === "address1") {
                            return (
                                <div className={"col-span-2"} key={fieldName}>
                                    <Text
                                        size="small"
                                        weight="regular"
                                        className={`mb-8 ${errors.billingAddress?.[fieldName]?.ref ? "text-red-500" : "text-black"}`}
                                    >
                                        <label
                                            htmlFor={fieldName}
                                            className={`mb-8 ${
                                                (errors?.billingAddress as any)?.[
                                                    fieldName
                                                ]?.ref
                                                    ? "text-red-500"
                                                    : "text-black"
                                            }`}
                                        >
                                            {!errors?.billingAddress?.address1 &&
                                                t(
                                                    langCode,
                                                    fieldLabel.get(fieldName) ?? ""
                                                )}{" "}
                                            {!field.required
                                                ? " " + t(langCode, "(Optional)")
                                                : ""}
                                            {(errors?.billingAddress as any)?.[
                                                fieldName
                                            ] &&
                                                t(
                                                    langCode,
                                                    (errors?.billingAddress as any)?.[
                                                        fieldName
                                                    ]?.message ?? ""
                                                )}
                                        </label>
                                    </Text>
                                    <div
                                        ref={el =>
                                            (fieldRefs.current[
                                                `billingAddress.${fieldName}`
                                            ] = el)
                                        }
                                    >
                                        <Autocomplete
                                            onLoad={onLoad}
                                            fields={["address_components"]}
                                            options={autoCompleteOptions}
                                            onPlaceChanged={onPlaceChanged}
                                        >
                                            <TextField
                                                type={"text"}
                                                value={inputValue}
                                                // {...register("billingAddress.address1")}
                                                placeholder={t(
                                                    langCode,
                                                    fieldPlaceholder.get("address1") ?? ""
                                                )}
                                                onChange={handleInputChange}
                                                theme="light"
                                                id={"address1"}
                                                name={"billingAddress.address1"}
                                                aria-label={"address1"}
                                                required={field.required}
                                                className={`${(errors?.billingAddress as any)?.address1?.ref ? "border-red-500" : ""}`}
                                            />
                                        </Autocomplete>
                                        <div className="pt-6 font-tradeLight text-12">
                                            <div
                                                className={
                                                    "mr-6 inline-flex h-16 w-16 select-none items-center justify-center rounded-lg border border-solid border-black text-center"
                                                }
                                            >
                                                &#33;
                                            </div>
                                            {t(langCode, "Address-help-text")}
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                        if (!field.required && fieldName !== "phoneNumber") return
                        return (
                            <div className={"col-span-2"} key={fieldName}>
                                <Text
                                    size="small"
                                    weight="regular"
                                    className={`mb-8 ${errors.billingAddress?.[fieldName]?.ref ? "text-red-500" : "text-black"}`}
                                >
                                    <label
                                        htmlFor={fieldName}
                                        className={`mb-8 ${
                                            (errors?.billingAddress as any)?.[fieldName]
                                                ?.ref
                                                ? "text-red-500"
                                                : "text-black"
                                        }`}
                                    >
                                        {!(errors.billingAddress as any)?.[fieldName] &&
                                            t(
                                                langCode,
                                                fieldLabel.get(fieldName) ?? ""
                                            )}{" "}
                                        {!field.required
                                            ? " " + t(langCode, "(Optional)")
                                            : ""}
                                        {(errors?.billingAddress as any)?.[fieldName] &&
                                            t(
                                                langCode,
                                                (errors?.billingAddress as any)?.[
                                                    fieldName
                                                ]?.message ?? ""
                                            )}
                                    </label>
                                </Text>
                                <div
                                    ref={el =>
                                        (fieldRefs.current[
                                            `billingAddress.${fieldName}`
                                        ] = el)
                                    }
                                >
                                    <TextField
                                        {...register(`billingAddress.${fieldName}`)}
                                        placeholder={t(
                                            langCode,
                                            fieldPlaceholder.get(fieldName) ?? ""
                                        )}
                                        theme="light"
                                        id={`billingAddress.${fieldName}`}
                                        aria-label={t(langCode, fieldName)}
                                        value={
                                            (customerInfo?.billingAddress as any)?.[
                                                fieldName
                                            ] ?? ""
                                        }
                                        onChange={e => {
                                            updateCustomerInfo({
                                                billingAddress: {
                                                    ...customerInfo?.billingAddress,
                                                    [fieldName]: e.target.value
                                                }
                                            })
                                            switch (fieldName) {
                                                case "firstName":
                                                    setValue(
                                                        "billingAddress.firstName",
                                                        e.target.value
                                                    )
                                                    break
                                                case "lastName":
                                                    setValue(
                                                        "billingAddress.lastName",
                                                        e.target.value
                                                    )
                                                    break
                                                case "phoneNumber":
                                                    setValue(
                                                        "billingAddress.phoneNumber",
                                                        e.target.value
                                                    )
                                                    break
                                                case "address1":
                                                    setValue(
                                                        "billingAddress.address1",
                                                        e.target.value
                                                    )
                                                    break
                                                case "address2":
                                                    setValue(
                                                        "billingAddress.address2",
                                                        e.target.value
                                                    )
                                                    break
                                                case "city":
                                                    setValue(
                                                        "billingAddress.city",
                                                        e.target.value
                                                    )
                                                    break
                                                case "zipCode":
                                                    setValue(
                                                        "billingAddress.zipCode",
                                                        e.target.value
                                                    )
                                                    break
                                                // Add more cases as needed
                                                default:
                                                    break
                                            }
                                        }}
                                        className={`w-full rounded-full border px-24 py-16 ${errors.billingAddress?.[fieldName]?.ref ? "border-red-500" : ""}`}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </>
    )
}
